import type { DeepReadonly, ToRefs } from 'vue'
import { FetchError } from 'ofetch'
import { isInIframe } from '@/lib/utils'
import { EmbedType } from '@/entities/embed'
import type { DeckWithEmbed } from '@/entities/embed'
import type { IncludeCompany } from '~/entities/deck'

export class UseEmbeddedDecksAuthError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'AuthError'
  }
}

export class UseEmbeddedDecksPermissionsError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'PermissionsError'
  }
}

export class UseEmbeddedDecksNotFoundError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'NotFoundError'
  }
}

type UseEmbeddedDecksError = UseEmbeddedDecksAuthError | UseEmbeddedDecksPermissionsError | UseEmbeddedDecksNotFoundError | Error

interface UseEmbeddedDecksState {
  loading: boolean
  error: UseEmbeddedDecksError | null
  deck: (DeckWithEmbed & IncludeCompany) | null
  isAnimatedDeck: boolean
  deckRenderState: {
    displayDeck: boolean
    displayTeaserDeck: boolean
  }
}

export interface UseEmbeddedDecks extends ToRefs<DeepReadonly<UseEmbeddedDecksState>> {
  closeLightbox: () => void
  openLightbox: () => void
  showDeck: (which: 'displayDeck' | 'displayTeaserDeck') => void
}

export const ACTIONS = ['VRIFY_MOUNTED', 'VRIFY_OPEN_LIGHTBOX', 'VRIFY_CLOSE_LIGHTBOX'] as const
export type ActionType = (typeof ACTIONS)[number]

// TODO: After initial implementation of deck messaging etc, revisit this to spike Marrion's proposal for generalizing this hook to support Video embeds (and possible others)
export function useEmbeddedDecks(embedId: string | number): UseEmbeddedDecks {
  const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))
  const { $analytics } = useNuxtApp()

  const state = reactive<UseEmbeddedDecksState>({
    loading: true,
    error: null,
    deck: null,
    isAnimatedDeck: false,
    deckRenderState: {
      displayDeck: false,
      displayTeaserDeck: false,
    },
  })

  onMounted(async () => {
    try {
      const embeddedDeck = await $fetch<DeckWithEmbed & IncludeCompany>(`v2/embed/decks/${embedId}`, {
        ...useFetchSharedOptions(),
        params: {
          include: ['company', 'partner_name'],
        },
        headers: {
          ...globalFetchHeaders.value,
          'X-Embed-Referring-URL': document.referrer,
          'X-Embedded-Content': 'deck',
          'X-Deck-Id': `${embedId}`,
        },
      })

      state.deck = embeddedDeck
      state.isAnimatedDeck = embeddedDeck.embed_type === EmbedType.Animated || embeddedDeck.embed_type === EmbedType.Partner

      if (state.isAnimatedDeck) {
        showDeck('displayTeaserDeck')
      }

      setupEmbeddedIframe()

      globalFetchHeaders.value['X-Embed-Referring-URL'] = document.referrer
      globalFetchHeaders.value['X-Embedded-Content'] = 'deck'
      globalFetchHeaders.value['X-Deck-Id'] = `${embeddedDeck.id || null}`
    } catch (e) {
      if (e instanceof FetchError) {
        if (e.response?.status === 401) {
          state.error = new UseEmbeddedDecksAuthError(e.data.message)
        } else if (e.response?.status === 403) {
          state.error = new UseEmbeddedDecksPermissionsError(e.data.message)
        } else if (e.response?.status === 404) {
          state.error = new UseEmbeddedDecksNotFoundError(e.data.message)
        } else {
          state.error = e
        }
      }
    } finally {
      state.loading = false
    }
  })

  onUnmounted(() => {
    delete globalFetchHeaders.value['X-Embed-Referring-URL']
    delete globalFetchHeaders.value['X-Embedded-Content']
    delete globalFetchHeaders.value['X-Deck-Id']

    window.removeEventListener('message', handleIframeHostMessages)
  })

  function setupEmbeddedIframe() {
    if (!isInIframe()) return

    if (state.isAnimatedDeck) {
      window.addEventListener('message', handleIframeHostMessages)
    }

    _postToHost('VRIFY_MOUNTED')
  }

  function handleIframeHostMessages(event: MessageEvent<{ type: ActionType }>) {
    if (!event.data.type) return

    if (event.data.type === 'VRIFY_CLOSE_LIGHTBOX') {
      showDeck('displayTeaserDeck')

      $analytics?.track('animatedEmbedClickClose', {
        deckId: state.deck?.id,
        deckName: state.deck?.title,
        companyId: state.deck?.company?.id,
        companyName: state.deck?.company?.name,
        embedLocation: state.deck?.embed_name,
      })
    }
  }

  function openLightbox() {
    showDeck('displayDeck')
    _postToHost('VRIFY_OPEN_LIGHTBOX')
  }

  function closeLightbox() {
    showDeck('displayTeaserDeck')
    _postToHost('VRIFY_CLOSE_LIGHTBOX')
    $analytics?.track('animatedEmbedClickClose', {
      deckId: state.deck?.id,
      deckName: state.deck?.title,
      companyId: state.deck?.company?.id,
      companyName: state.deck?.company?.name,
      embedLocation: state.deck?.embed_name,
    })
  }

  function showDeck(which: 'displayDeck' | 'displayTeaserDeck') {
    state.deckRenderState = {
      ...state.deckRenderState,
      displayDeck: which === 'displayDeck',
      displayTeaserDeck: which === 'displayTeaserDeck',
    }
  }

  // TODO: when we get around to generalizing this code for other embeds, this may have to be extracted
  function _postToHost(type: ActionType): void {
    const payload = _createPayload(type)
    window.parent.postMessage(payload, '*')
  }

  function _createPayload(type: ActionType) {
    if (!state.deck) throw new Error('No teaser deck found')
    if (!embedId) throw new Error('No deck id provided')

    return {
      type,
      deckId: state.deck.id,
      embedId: embedId,
      embedType: state.deck.embed_type,
    }
  }

  return { ...toRefs(readonly(state)), closeLightbox, openLightbox, showDeck }
}
